import { Recruitment, User, recruitmentStatusConfig, actions } from 'entities';
import { useUser } from 'hooks/user';
import { useOwnershipChange } from 'providers/OwnershipChange';
import { getRecruitmentActions } from '../components/RecruitmentModal/permissions';

type BaseRecruitment = Pick<Recruitment, 'status' | 'id'> & {
  owner: User;
};

interface RecruitmentOwnerChangeParams {
  recruitment: BaseRecruitment;
  onNewOwner: (user: User) => void;
}

interface RecruitmentOwnerChangeHook {
  changeRecruitmentOwner: () => void;
  canChangeRecruitmentOwner: (recruitment: BaseRecruitment) => boolean;
  loading: boolean;
}

export function useRecruitmentOwnerChange({
  onNewOwner,
  recruitment,
}: RecruitmentOwnerChangeParams): RecruitmentOwnerChangeHook {
  const user = useUser();

  const { openChangeOwnerModal } = useOwnershipChange();

  const assignRecruitment = actions.projects.assignRecruitment.use();

  const changeOwner = actions.projects.changeRecruitmentOwner.use();

  const isAssignment = recruitment.status === recruitmentStatusConfig.unassigned.code;

  const changeRecruitmentOwner = (): void => {
    openChangeOwnerModal({
      currentOwner: recruitment.owner,

      text: '',

      requireJustification: isAssignment ? false : true,

      onConfirm: async (newOwner, justification) => {
        const ok = await (isAssignment
          ? assignRecruitment.execute({ recruitmentId: recruitment.id, owner: newOwner!.id })
          : changeOwner.execute({
              recruitmentId: recruitment.id,
              owner: newOwner!.id,
              justification,
            }));

        if (ok) onNewOwner(newOwner!);
      },
    });
  };

  const canChangeRecruitmentOwner = (recruitment: BaseRecruitment): boolean => {
    return getRecruitmentActions(recruitment, user).canAssign;
  };

  return {
    changeRecruitmentOwner,

    canChangeRecruitmentOwner,

    loading: changeOwner.loading,
  };
}
